import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, useApi, errMsg } from "../lib/api";
import { PageHeader, DataState, Field, Spinner } from "../components/kit";
import { ColorField, UploadButton, EmailPreview } from "./EmailParts";

const SAMPLE = "Hi {{first_name}},\n\nThanks for reaching out! Your order #1042 shipped this morning and should arrive within 3–5 business days.\n\nIf anything looks off, just reply to this email and our team will help.";
const COLORS = [["primary_color", "Primary / buttons"], ["button_text_color", "Button text"], ["background_color", "Page background"], ["card_color", "Email card"], ["text_color", "Text"], ["muted_color", "Footer text"]];
const SOCIAL = ["website", "twitter", "linkedin", "instagram", "facebook"];

function Section({ title, children }) {
  return <section className="border-b border-[#EEF2F7] pb-5 mb-5 last:border-0"><h3 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-3">{title}</h3>{children}</section>;
}

function LogoSection({ b, set }) {
  return (
    <Section title="Logo">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-16 w-36 rounded-md border border-dashed border-[#CBD5E1] bg-white grid place-items-center overflow-hidden" data-testid="brand-logo-preview">{b.logo_url ? <img src={b.logo_url} alt="Logo" className="max-h-14 max-w-[130px] object-contain" /> : <span className="text-[11px] muted">No logo</span>}</div>
        <div className="flex flex-col gap-1.5"><UploadButton kind="logo" label={b.logo_url ? "Replace logo" : "Upload logo"} onUploaded={(url) => set({ logo_url: url })} testid="brand-logo-upload" />{b.logo_url && <button className="btn-ghost btn-sm text-red-600" onClick={() => set({ logo_url: "" })} data-testid="brand-logo-remove"><Trash2 size={12} />Remove</button>}</div>
      </div>
      <Field label={`Logo width: ${b.logo_width}px`}><input type="range" min={40} max={240} value={b.logo_width} onChange={(e) => set({ logo_width: Number(e.target.value) })} className="w-full accent-[#0A0A0A]" data-testid="brand-logo-width" /></Field>
      <div className="flex gap-1.5 mt-2">{["left", "center"].map((a) => <button key={a} onClick={() => set({ header_align: a })} className={`btn-sm btn ${b.header_align === a ? "bg-[#0A0A0A] text-white" : "btn-secondary"}`} data-testid={`brand-align-${a}`}>Align {a}</button>)}</div>
    </Section>
  );
}

function StyleSection({ b, set, fonts }) {
  return (
    <>
      <Section title="Colors">{COLORS.map(([k, l]) => <ColorField key={k} label={l} value={b[k]} onChange={(v) => set({ [k]: v })} testid={`brand-${k}`} />)}</Section>
      <Section title="Typography & shape">
        <Field label="Font"><select className="input" value={b.font} onChange={(e) => set({ font: e.target.value })} data-testid="brand-font">{fonts.map((f) => <option key={f}>{f}</option>)}</select></Field>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label={`Button corners: ${b.button_radius}px`}><input type="range" min={0} max={28} value={b.button_radius} onChange={(e) => set({ button_radius: Number(e.target.value) })} className="w-full accent-[#0A0A0A]" data-testid="brand-button-radius" /></Field>
          <Field label={`Card corners: ${b.card_radius}px`}><input type="range" min={0} max={24} value={b.card_radius} onChange={(e) => set({ card_radius: Number(e.target.value) })} className="w-full accent-[#0A0A0A]" data-testid="brand-card-radius" /></Field>
        </div>
        <p className="text-[11px] muted mt-2">Web-safe fonts only, so every inbox shows them correctly.</p>
      </Section>
    </>
  );
}

function FooterSection({ b, set }) {
  return (
    <>
      <Section title="Footer">
        <div className="space-y-3">
          <Field label="Footer text"><input className="input" value={b.footer} onChange={(e) => set({ footer: e.target.value })} data-testid="brand-footer" /></Field>
          <Field label="Company address"><input className="input" value={b.address} onChange={(e) => set({ address: e.target.value })} data-testid="brand-address" /></Field>
          <Field label="Contact email"><input className="input" value={b.contact_email} onChange={(e) => set({ contact_email: e.target.value })} data-testid="brand-contact-email" /></Field>
        </div>
      </Section>
      <Section title="Social links">
        <div className="space-y-2">{SOCIAL.map((k) => <div key={k} className="flex items-center gap-2"><span className="w-20 text-[13px] capitalize text-[#334155]">{k}</span><input className="input h-8 text-xs" placeholder="https://" value={b.socials[k]} onChange={(e) => set({ socials: { ...b.socials, [k]: e.target.value } })} data-testid={`brand-social-${k}`} /></div>)}</div>
      </Section>
    </>
  );
}

export default function BrandKit() {
  const q = useApi("/email/brand");
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (q.data) setB(q.data.brand); }, [q.data]);
  const set = (patch) => setB((x) => ({ ...x, ...patch }));
  const dirty = b && q.data && JSON.stringify(b) !== JSON.stringify(q.data.brand);
  const save = async () => { setBusy(true); try { await api.put("/email/brand", b); toast.success("Brand kit saved. All new emails use it."); q.reload(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  return (
    <div className="p-6 max-w-[1400px]" data-testid="brand-kit-page">
      <PageHeader title="Brand Kit" description="Your logo, colors and footer, applied to every support reply and template you send." actions={<><button className="btn-ghost" disabled={!dirty} onClick={() => setB(q.data.brand)} data-testid="brand-reset">Discard</button><button className="btn-primary" disabled={!dirty || busy} onClick={save} data-testid="brand-save">{busy && <Spinner />}Save brand</button></>} />
      <DataState q={q}>{b && <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
        <div className="card p-5" data-testid="brand-form"><LogoSection b={b} set={set} /><StyleSection b={b} set={set} fonts={q.data.fonts} /><FooterSection b={b} set={set} /></div>
        <div className="lg:sticky lg:top-4 h-[calc(100vh-140px)] min-h-[620px]"><EmailPreview payload={{ subject: "Your order is on its way", body: SAMPLE, brand: b, variables: { first_name: "Jordan" } }} testid="brand-preview" /></div>
      </div>}</DataState>
    </div>
  );
}
